/*
  Третья колонка шапки счёта: что сказал и сделал должник — §3.5, §3.8.

  Заведена 25.09.2026. Владелец открыл боевой счёт, где должник признал долг и
  получил ссылку на свою страницу, и не увидел на первом экране ни признания,
  ни выданной ссылки, ни того, открывал ли он её. Карточка отвечала только на
  вопрос «сколько денег».

  🔴 Пустые ответы не прячутся - НО только там, где должника спрашивали. «Срок
  оплаты не назвал» и «перезвонить не просил» - такие же ответы оператору, как
  названная дата, когда разговор состоялся. По счёту, к которому не обращались
  ни разу, те же строки утверждают непроверенное и читаются как итог
  состоявшегося разговора: решение владельца 28.09.2026 - тогда колонка не
  показывает ответов вовсе и говорит, чего ещё не было. Решает это сервер:
  пустой список сигналов и означает «взаимодействия не было».

  🔴 Кабинет ничего не додумывает. Каждый ответ стоит за записанным фактом -
  состоянием обращения, обещанием, выданной ссылкой, её открытием, назначенным
  днём звонка, - и подписи к ним собирает сервер (§4.5.1). Шансов оплаты,
  «вероятности возврата» и прочих правдоподобных цифр здесь нет и быть не
  может: считать их не из чего, а решение «подавать или нет» принимают по этому
  экрану.
*/

import { dateTime } from '../api/format.js'
import InfoHint from './InfoHint.jsx'

/*
  Значок ответа. Три вида вместо четырёх из макета: «не открыл страницу» и «не
  назвал срок» различаются там рисунком, но сервер это различие машинно не
  отдаёт, а придумывать его в кабинете значило бы завести здесь свой разбор
  смысла ответов. Минус - знак «−», а не тире: длинное тире в кабинете
  запрещено, а дефис в кружке теряется.
*/
const MARKS = { yes: '✓', no: '−', waiting: '…' }

/*
  🔴 Открытый вопрос В-23 реестра, и до ответа юриста он живёт ровно здесь - на
  экране, рядом с самим признанием. В расчёт срока давности признание не идёт:
  прерывает ли устное слово течение срока по ст. 203 ГК, неизвестно, и
  подставить сюда ответ «да» значило бы подать заявление по сроку, которого
  может не быть.
*/
const ACKNOWLEDGED_HINT =
  'Признание записано со слов должника в разговоре, оно есть в расшифровке. На ' +
  'расчёт срока исковой давности оно не влияет: прерывает ли устное признание ' +
  'течение срока (ст. 203 ГК РФ) - вопрос юристу, ответа пока нет.'

export default function AccountSignals({ signals }) {
  return (
    <div className="summary__col">
      <div className="summary__cap">Что сказал и сделал должник</div>

      {/*
        Пустое состояние объясняет, чего ещё не было и что появится здесь
        (§4.5.4). Молчащая колонка без единого слова читалась бы как поломка, а
        ответы «нет» по счёту без обращений - как итог разговора, которого не
        было.
      */}
      {signals.length === 0 ? (
        <div className="signals__empty">
          <p>С должником ещё не разговаривали, ссылку на его страницу не выдавали.</p>
          <p>
            Здесь появится то, что он скажет и сделает: признал ли долг, назвал ли срок оплаты,
            открывал ли свою страницу.
          </p>
        </div>
      ) : null}

      {signals.map((signal) => (
        <div key={signal.key} className={`signal signal--${signal.answer}`}>
          <span className="signal__mark" aria-hidden="true">
            {MARKS[signal.answer]}
          </span>
          <div>
            <div className="signal__title">
              {signal.title}
              {signal.key === 'acknowledged' && signal.answer === 'yes' ? (
                <InfoHint title="Что это значит" text={ACKNOWLEDGED_HINT} />
              ) : null}
            </div>
            {/*
              Пояснение и время стоят одной строкой: порознь они дали бы два
              этажа мелкого текста под каждым ответом, а колонка читается
              сверху вниз как рассказ о том, чем кончился контакт.
            */}
            {note(signal) ? <div className="signal__note">{note(signal)}</div> : null}
          </div>
        </div>
      ))}
    </div>
  )
}

/* Пояснение к ответу: что известно и когда это было. */
function note(signal) {
  return [signal.note, signal.when ? dateTime(signal.when) : null].filter(Boolean).join(', ')
}
